import React, { ReactNode } from 'react';

import { Grid, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material';
import { Link } from 'react-router-dom';

import ChevronTopIcon from '~/assets/icons/ChevronTopIcon';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { CssUtilityType } from '~/styled';
import { BIG_MARGIN, LARGE_MARGIN, MIDDLE_MARGIN_PX, SMALL_MARGIN_PX } from '~/theme';

interface HeaderWithNavigationProps {
  setView?: Function;
  url?: string | null;
  title: string;
  titleIcon?: React.ReactNode;
  wrapperCss?: CssUtilityType;
  titleWrapperCss?: CssUtilityType;
  titleCss?: CssUtilityType;
  navLinkCss?: CssUtilityType;
  children?: ReactNode;
}

const HeaderWithNavigation: React.FC<HeaderWithNavigationProps> = ({
  setView,
  title,
  titleIcon,
  url,
  wrapperCss,
  titleWrapperCss,
  titleCss,
  navLinkCss,
  children,
}) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();

  return (
    <StyledGrid container alignItems="center" optionalStyles={wrapperCss}>
      <TitleWrapper optionalStyles={titleWrapperCss}>
        {url ? (
          <StyledLink
            aria-label={t('button.goBack')}
            data-testid="backLink"
            to={url}
            optionalStyles={navLinkCss}
          >
            <StyledChevronTopIcon
              color={theme.palette.common.blue}
              onClick={() => setView && setView()}
            />
          </StyledLink>
        ) : setView ? (
          <ArrowWrapper optionalStyles={navLinkCss}>
            <StyledChevronTopIcon
              color={theme.palette.common.blue}
              onClick={setView}
              data-testid="backButton"
            />
          </ArrowWrapper>
        ) : null}
        {titleIcon}
        {title && (
          <Title data-testid="aslTitle" variant="h1" optionalStyles={titleCss}>
            {title}
          </Title>
        )}
      </TitleWrapper>
      {children}
    </StyledGrid>
  );
};

export default HeaderWithNavigation;

const StyledGrid = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})<{
  optionalStyles?: CssUtilityType;
}>`
  margin-bottom: ${MIDDLE_MARGIN_PX};
  position: relative;
  justify-content: space-between;

  ${({ theme }) => theme.breakpoints.down('md')} {
    margin-bottom: ${SMALL_MARGIN_PX};
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding-top: ${({ theme }) => theme.isSsku && LARGE_MARGIN + BIG_MARGIN + 'px'};
  }

  ${({ optionalStyles }) => optionalStyles}
`;
const TitleWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})<{ optionalStyles?: CssUtilityType }>`
  position: relative;
  display: flex;
  align-items: center;
  max-width: 100%;
  ${({ optionalStyles }) => optionalStyles}
`;
const createStyledLink = (tag) => styled(tag, {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})`
  position: relative;
  top: 5px;

  ${({ theme }) => theme.breakpoints.up('xl')} {
    position: absolute;
    top: ${({ theme }) => (theme.isSsku ? SMALL_MARGIN_PX : 0)};
  }

  ${({ optionalStyles }) => optionalStyles}
`;
const StyledChevronTopIcon = styled(ChevronTopIcon)`
  height: 50px;
  cursor: pointer;
  left: -50px;
  padding: 15px;
  position: absolute;
  transform: rotate(-90deg);
  width: 50px;
  color: ${({ theme }) => theme.palette.common.accentMain};

  ${({ theme }) => theme.breakpoints.down('xl')} {
    position: relative;
    left: -10px;
    width: 40px;
    height: 40px;
    padding: 12px;
  }
`;
const StyledLink = createStyledLink(Link);
const ArrowWrapper = createStyledLink('div');

const Title = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})<{
  optionalStyles?: CssUtilityType;
}>`
  font-family: ${({ theme }) => theme.typography.specialFontFamily};
  font-size: ${({ theme }) => theme.isSsku && '40px'};

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: ${({ theme }) => theme.isSsku && '72px'};
  }

  & {
    line-height: 1.2;
  }

  ${({ optionalStyles }) => optionalStyles}
`;
